import { useLayoutEffect, useMemo, useRef, type ReactNode } from "react";
import {
  useAppEnvironmentContextStore,
  useAppEnvironmentScopeGeneration,
} from "./AIChatProvider";
import type {
  SBotAppEnvironmentSurfaceReport,
} from "./services/appEnvironmentContext";

export type AppEnvironmentOwningTab = "home" | "league" | "account" | "admin";

export interface AppEnvironmentRouteContext {
  path: string;
  identity: string;
  owningTab: AppEnvironmentOwningTab;
  breadcrumbs: readonly string[];
  destinationType: string;
  title: string;
  state?: "visible" | "form; unsaved values undisclosed" | "resolving destination";
}

/**
 * Authenticated AppShell destinations where floating chat is available.
 *
 * Keep this closed manifest in step with router.tsx. It intentionally describes
 * only route-level semantics; screens may register a newer, richer report from
 * already-authorized data without changing this coverage baseline.
 */
export const APP_ENVIRONMENT_ROUTE_CONTEXTS = [
  route("/home", "home", "Home", "home"),
  route("/app/*", "home", "Opening App Destination", "appRoute", "resolving destination"),
  route("/home/activity", "home", "Recent Activity", "recentActivity"),
  route("/home/activity/:eventId", "home", "Activity Detail", "activityDetail"),
  route("/home/schedule", "home", "Schedule", "schedule"),
  route("/home/organizations", "home", "Organizations", "organizations"),
  route("/home/athletes", "home", "Athletes", "athletes"),
  route("/home/staff", "home", "League Staff", "leagueStaff"),
  route("/home/athletes/:athleteID", "home", "Athlete Profile", "athlete"),
  route("/home/staff/:summaryID", "home", "Staff Profile", "staff"),
  route("/home/gps", "home", "GPS", "gpsMenu"),

  route("/league", "league", "League", "league"),
  route("/league/settings", "league", "League Settings", "leagueSettings", "form; unsaved values undisclosed"),
  route("/league/invitations", "league", "League Invitations", "leagueInvitations"),
  route("/league/athletes", "league", "Athlete Schema", "athleteSchema", "form; unsaved values undisclosed"),
  route("/league/athletes/new", "league", "New Athlete", "athleteCreation", "form; unsaved values undisclosed"),
  route("/league/external-sources", "league", "External Sources", "externalSources", "form; unsaved values undisclosed"),
  route("/league/competitions", "league", "Competition Schema", "competitionSchema", "form; unsaved values undisclosed"),
  route("/league/competitions/new", "league", "New Competition", "competitionCreation", "form; unsaved values undisclosed"),
  route("/league/org-admin", "league", "Organization Admin Schema", "organizationAdminSchema", "form; unsaved values undisclosed"),
  route("/league/course-encoder", "league", "Course Encoder", "courseEncoder", "form; unsaved values undisclosed"),

  route("/competitions/:instanceId", "home", "Competition", "competition"),
  route("/competitions/:instanceId/courses", "home", "Courses", "courses"),
  route("/competitions/:instanceId/gps/map", "home", "GPS Map", "gpsMap"),
  route("/competitions/:instanceId/gps/assignments", "home", "GPS Assignments", "gpsAssignments", "form; unsaved values undisclosed"),
  route("/competitions/:instanceId/gps/share", "home", "Share Live Map", "gpsShare"),
  route("/competitions/:instanceId/gps/devices/:deviceId", "home", "GPS Device", "gpsDevice"),
  route("/competitions/:instanceId/gps/history", "home", "GPS History", "gpsHistory"),
  route("/competitions/:instanceId/gps/data", "home", "GPS Data", "gpsData"),
  route("/competitions/:instanceId/overlays", "home", "Broadcast Overlays", "broadcastOverlays"),
  route("/competitions/:instanceId/overlays/:overlayId", "home", "Overlay Designer", "overlay", "form; unsaved values undisclosed"),

  route("/organizations/new", "home", "New Organization", "organizationCreation", "form; unsaved values undisclosed"),
  route("/organizations/:organizationId", "home", "Organization", "organization", "form; unsaved values undisclosed"),

  route("/account", "account", "Account", "account"),
  route("/account/notifications", "account", "Notifications", "notifications"),
  route("/account/notifications/:notificationID", "account", "Notification", "notificationDetail"),
  route("/account/support", "account", "Support", "requestSupport", "form; unsaved values undisclosed"),
  route("/account/support/:requestID", "account", "Support Request", "supportRequest"),
  route("/account/settings", "account", "Settings", "accountSettings", "form; unsaved values undisclosed"),

  route("/admin", "admin", "SBot Labs Admin", "sBotAdminHome"),
  route("/admin/espn-access", "admin", "ESPN Access Requests", "espnAccessRequests"),
  route("/admin/support", "admin", "Support Inbox", "supportInbox"),
  route("/admin/support/:requestID", "admin", "Support Request", "adminSupportRequest"),
  route("/admin/billing", "admin", "Billing", "billing"),
  route("/admin/league-billing", "admin", "League Billing", "leagueBilling", "form; unsaved values undisclosed"),
  route("/admin/ai-usage", "admin", "AI Usage Limits", "aiUsageLimits", "form; unsaved values undisclosed"),
] as const satisfies readonly AppEnvironmentRouteContext[];

/** Routes that must never register app-environment context. */
export const APP_ENVIRONMENT_CONTEXT_EXCLUSIONS = [
  { path: "/", reason: "redirect-only" },
  { path: "/auth", reason: "authentication" },
  { path: "/onboarding", reason: "onboarding" },
  { path: "/overlay/:overlayId", reason: "public output" },
  { path: "/league-overlay/:outputId", reason: "public output" },
  { path: "/share/gps/:shareId", reason: "public output" },
  { path: "/ai", reason: "dedicated AI" },
  { path: "*", reason: "redirect-only" },
] as const;

export type AppEnvironmentRoutePath = (typeof APP_ENVIRONMENT_ROUTE_CONTEXTS)[number]["path"];

export function appEnvironmentRouteContext(
  path: AppEnvironmentRoutePath,
): AppEnvironmentRouteContext {
  const context = APP_ENVIRONMENT_ROUTE_CONTEXTS.find((candidate) => candidate.path === path);
  if (!context) {
    throw new Error(`Missing app-environment route context for ${path}.`);
  }
  return context;
}

export function AppEnvironmentScreen({
  route: routePath,
  children,
}: {
  route: AppEnvironmentRoutePath;
  children: ReactNode;
}) {
  const context = appEnvironmentRouteContext(routePath);
  const isTabRoot = routePath === "/home"
    || routePath === "/league"
    || routePath === "/account"
    || routePath === "/admin";
  const report = useMemo<SBotAppEnvironmentSurfaceReport>(() => ({
    identity: context.identity,
    owningTab: context.owningTab,
    breadcrumbs: [...context.breadcrumbs],
    destinationType: context.destinationType,
    surfaceLevel: isTabRoot ? "tabRoot" : "routePlaceholder",
    surfaceDepth: isTabRoot ? 0 : Math.max(1, context.breadcrumbs.length - 1),
    surfacePresentation: isTabRoot ? "tabRoot" : "routeDestination",
    screen: {
      title: context.title,
      state: context.state ?? "visible",
      contextCompleteness: "basic",
      sections: [{
        id: "current-view",
        title: "Current View",
        controls: [{
          id: "current-view-title",
          role: "status",
          label: context.title,
        }],
      }],
    },
  }), [context, isTabRoot]);

  useAppEnvironmentReport(report);

  return children;
}

/** Publishes explicit, already-authorized screen semantics for the mounted view. */
export function useAppEnvironmentReport(report: SBotAppEnvironmentSurfaceReport): void {
  const store = useAppEnvironmentContextStore();
  const scopeGeneration = useAppEnvironmentScopeGeneration();
  const reportRef = useRef(report);
  reportRef.current = report;
  const tokenRef = useRef<string | null>(null);

  useLayoutEffect(() => {
    if (!store) return;
    const token = store.register(reportRef.current);
    tokenRef.current = token;
    return () => {
      store.unregister(token);
      if (tokenRef.current === token) tokenRef.current = null;
    };
  }, [store, scopeGeneration]);

  useLayoutEffect(() => {
    const token = tokenRef.current;
    if (store && token) store.update(token, report);
  }, [report, store]);
}

function route(
  path: string,
  owningTab: AppEnvironmentOwningTab,
  title: string,
  destinationType: string,
  state?: AppEnvironmentRouteContext["state"],
): AppEnvironmentRouteContext {
  return {
    path,
    identity: `route:${destinationType}`,
    owningTab,
    breadcrumbs: [tabTitle(owningTab), title],
    destinationType,
    title,
    state,
  };
}

function tabTitle(tab: AppEnvironmentOwningTab): string {
  switch (tab) {
    case "home": return "Home";
    case "league": return "League";
    case "account": return "Account";
    case "admin": return "Admin";
  }
}
